import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('popmap_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      localStorage.removeItem('popmap_token');
      localStorage.removeItem('popmap_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const testConnection = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Connection failed'
    };
  }
};

export const authAPI = {
  googleLogin: (googleData) => api.post('/api/auth/google', googleData),
  demoLogin: (demoData) => api.post('/api/auth/demo', demoData),
  getProfile: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
  refreshToken: () => api.post('/api/auth/refresh')
};

export const mapsAPI = {
  getMaps: (params = {}) => api.get('/api/maps', { params }),
  getMap: (mapId) => api.get(`/api/maps/${mapId}`),
  createMap: (mapData) => api.post('/api/maps', mapData),
  updateMap: (mapId, mapData) => api.put(`/api/maps/${mapId}`, mapData),
  deleteMap: (mapId) => api.delete(`/api/maps/${mapId}`),
  getAnalytics: (mapId, params = {}) => api.get(`/api/maps/${mapId}/analytics`, { params }),
  getUnassigned: () => api.get('/api/maps/unassigned'),
  getArchivedUnassigned: () => api.get('/api/maps/unassigned/archived'),
  toggleArchive: (mapId) => api.put(`/api/maps/${mapId}/archive`),
  addCollaborator: (mapId, email) => api.post(`/api/maps/${mapId}/collaborators`, { email }),
  assignToEvent: (mapId, eventId) =>
    api.put(`/api/maps/${mapId}/assign`, { eventId }),
  removeCollaborator: (mapId, email) => api.delete(`/api/maps/${mapId}/collaborators/${encodeURIComponent(email)}`),
  getCollaborators: (mapId) => api.get(`/api/maps/${mapId}/collaborators`)
};

export const markersAPI = {
  getMarkers: (mapId, params = {}) => api.get(`/api/markers/${mapId}`, { params }),
  createMarker: (markerData) => api.post('/api/markers', markerData),
  updateMarker: (markerId, markerData) => api.put(`/api/markers/${markerId}`, markerData),
  deleteMarker: (markerId) => api.delete(`/api/markers/${markerId}`)
};

export const eventAPI = {
  getAll: () => api.get('/api/events'),
  getArchived: () => api.get('/api/events/archived'),
  getById: (eventId) => api.get(`/api/events/${eventId}`),
  create: (eventData) => api.post('/api/events', eventData),
  update: (eventId, eventData) => api.put(`/api/events/${eventId}`, eventData),
  addCollaborator: (eventId, email) =>
    api.put(`/api/events/${eventId}/collaborators`, { email }),
  removeCollaborator: (eventId, userId) =>
    api.delete(`/api/events/${eventId}/collaborators/${userId}`),
  getCollaborators: (eventId) =>
    api.get(`/api/events/${eventId}/collaborators`),
  toggleArchive: (eventId) => api.put(`/api/events/${eventId}/archive`),
  delete: (eventId) => api.delete(`/api/events/${eventId}`)
};

export const qrAPI = {
  getQRCodeUrl: (code, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_BASE_URL}/api/qr/${code}${queryString ? `?${queryString}` : ''}`;
  }
};

export default api;